/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IconButton } from '@fluentui/react/lib/Button';
import { IStackItemProps, IStackProps, Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { LuisText, LuisTextProps } from 'src/core/components/LuisText';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { partial } from 'src/core/utils/function';
import { EntityTreeFilterBox } from 'src/luis/verticals/labelingSidePane/components/EntityTreeFilterBox';
import { entityTreeHeaderHeight } from 'src/luis/verticals/labelingSidePane/constants';

const messages = defineMessages({
    title: {
        id: 'luis.entityTreeTopBar.title',
        defaultMessage: 'Label entities'
    },
    titleHelperText: {
        id: 'luis.entityTreeTopBar.titleHelperText',
        defaultMessage: `This pane lists only entities you can apply while labeling.
It does not show prebuilt or regex entities, which are exact matches, unless they have roles.`
    },
    entityLabeler: {
        id: 'luis.entityTreeTopBar.entityLabeler',
        defaultMessage: 'Entity labeler'
    },
    filter: {
        id: 'luis.entityTreeTopBar.filter',
        defaultMessage: 'Filter'
    },
    addEntity: {
        id: 'luis.entityTreeTopBar.addEntity',
        defaultMessage: 'Add entity'
    }
});

const { styleComponent } = getStylistV2('EntityTreeTopBar');

const Root = styleComponent<IStackProps>(Stack)('Root', {
    height: entityTreeHeaderHeight,
    padding: 12
});

const Title = styleComponent<IStackItemProps>(Stack.Item)('Title', theme => ({
    color: theme.titleColor,
    fontSize: 14,
    fontWeight: 600,
    lineHeight: '24px',
    display: 'flex',
    alignItems: 'center',
    minWidth: 0
}));

const TitleText = styleComponent<LuisTextProps>(LuisText)('TitleText', () => ({
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
}));

export type EntityTreeTopBarProps = {
    /**
     * Indicates if the top bar is disabled.
     */
    disabled?: boolean;
    /**
     * Entity filtering query.
     */
    query: string;
    /**
     * Indicates if the labeling is on.
     */
    isLabelingOn: boolean;
    /**
     * Indicates if the labeling button is disabled.
     */
    toggleLabelingButtonDisabled: boolean;
    /**
     * Callback to toggle the labeling.
     */
    onToggleLabelingButton?: (on: boolean) => void;
    /**
     * Callback to update the query.
     */
    onChangeQuery: (query: string) => void;
    /**
     * Callback to start entity creation.
     */
    onStartCreateEntity: () => void;
};

export const EntityTreeTopBar = (props: EntityTreeTopBarProps) => {
    const {
        disabled,
        isLabelingOn,
        toggleLabelingButtonDisabled,
        onToggleLabelingButton,
        query,
        onChangeQuery,
        onStartCreateEntity
    } = props;
    const { localize } = useLocalization();

    const [filterOn, setFilterOn] = React.useState(false);

    const dismiss = () => {
        setFilterOn(false);
        onChangeQuery('');
    };

    return (
        <Root grow verticalAlign="center" horizontal tokens={{ childrenGap: 8 }}>
            {!filterOn ? (
                <Title grow>
                    <TitleText title={localize(messages.title)} fontWeight={600}>
                        {localize(messages.title)}
                    </TitleText>
                    <CalloutButton content={localize(messages.titleHelperText)} />
                </Title>
            ) : null}

            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }} role="menu" grow={filterOn}>
                <LuisTooltip content={localize(messages.entityLabeler)}>
                    <IconButton
                        role="menuitem"
                        checked={isLabelingOn}
                        iconProps={{ iconName: 'Highlight' }}
                        ariaLabel={localize(messages.entityLabeler)}
                        disabled={disabled || toggleLabelingButtonDisabled}
                        onClick={partial(onToggleLabelingButton, !isLabelingOn)}
                    />
                </LuisTooltip>

                {filterOn ? (
                    <Stack.Item grow>
                        <EntityTreeFilterBox query={query} onChangeQuery={onChangeQuery} onDismiss={dismiss} />
                    </Stack.Item>
                ) : (
                    <>
                        <LuisTooltip content={localize(messages.filter)}>
                            <IconButton
                                role="menuitem"
                                disabled={disabled}
                                iconProps={{ iconName: 'Filter' }}
                                onClick={partial(setFilterOn, true)}
                                ariaLabel={localize(messages.filter)}
                            />
                        </LuisTooltip>

                        <LuisTooltip content={localize(messages.addEntity)}>
                            <IconButton
                                role="menuitem"
                                onClick={onStartCreateEntity}
                                iconProps={{ iconName: 'Add' }}
                                ariaLabel={localize(messages.addEntity)}
                            />
                        </LuisTooltip>
                    </>
                )}
            </Stack>
        </Root>
    );
};
